import { useInvalidation } from 'api-read-hook';
import { useForm } from 'react-typed-form';
import { Api, useApiFormSubmit } from 'src/api';
import { Modal, ModalBody, ModalFooter } from 'src/components';
import { useSelectedCompany } from 'src/contexts';
import { FieldRadioGroup, FieldText, SubmitButton } from 'src/form';
import { capitalize } from 'src/util';

type Deployment = Api.Environment.V['deployments'][number];

type Props = {
  environments: Api.Environment.V[];
  existing?: Deployment | null;
  defaultEnvironmentIri?: string | null;
  onClose: () => void;
};

type FormValues = {
  environment: string | null;
  name: string | null;
  url: string | null;
  visitorPassword: string | null;
  githubRepo: string | null;
  githubBranch: string | null;
  buildPlatform: string | null;
};

export default function NewDeploymentModal({
  environments,
  existing,
  defaultEnvironmentIri,
  onClose,
}: Props) {
  const { invalidateExact } = useInvalidation();
  const company = useSelectedCompany();

  // Default to the environment of the tab you're on, else the first one.
  const defaultEnvIri = defaultEnvironmentIri ?? environments[0]?.['@id'] ?? '';

  const submit = useApiFormSubmit(
    existing ? 'PATCH' : 'POST',
    existing
      ? `/environment-deployments/${existing.id}`
      : '/environment-deployments',
    {
      onSuccess: () => {
        onClose();
        invalidateExact(`/companies/${company.id}/environments`);
      },
    }
  );

  const form = useForm<FormValues>({
    defaultValues: {
      environment: defaultEnvIri,
      name: existing?.name ?? '',
      url: existing?.url ?? null,
      visitorPassword: existing?.visitorPassword ?? null,
      githubRepo: existing?.githubRepo ?? null,
      githubBranch: existing?.githubBranch ?? null,
      buildPlatform: existing?.buildPlatform ?? '',
    },
    onSubmit: (values, helpers) => {
      const { environment, ...rest } = values;
      // '' (the "None" option) normalises back to null for the API
      const payload = { ...rest, buildPlatform: rest.buildPlatform || null };
      // environment is only sent on create (PATCH can't move a deployment)
      return submit(existing ? payload : { environment, ...payload }, helpers);
    },
  });

  const envOptions = environments.map((env) => ({
    value: env['@id'],
    label: capitalize(env.slug),
  }));

  return (
    <Modal
      title={existing ? 'Edit Deployment' : 'New Deployment'}
      onClose={onClose}
      fullScreenOnMobile
    >
      <ModalBody>
        {!existing && (
          <FieldRadioGroup
            label="Environment"
            field={form.getField('environment')}
            options={envOptions}
          />
        )}

        <FieldText
          field={form.getField('name')}
          label="Name"
          placeholder="Enter a name"
          required
        />

        <FieldText
          field={form.getField('url')}
          label="URL (Optional)"
          placeholder="https://..."
        />

        <FieldText
          field={form.getField('visitorPassword')}
          label="Visitor password (Optional)"
          placeholder="Enter a visitor password"
        />

        <FieldText
          field={form.getField('githubRepo')}
          label="GitHub repo (Optional)"
          placeholder="e.g. hub-server"
        />

        <FieldText
          field={form.getField('githubBranch')}
          label="GitHub branch (Optional)"
          placeholder="e.g. staging"
        />

        <FieldRadioGroup
          label="Build platform (Optional)"
          field={form.getField('buildPlatform')}
          options={[
            { value: '', label: 'None' },
            { value: 'ios', label: 'iOS' },
            { value: 'android', label: 'Android' },
          ]}
        />
      </ModalBody>

      <ModalFooter>
        <SubmitButton {...form} label={existing ? 'Save' : 'Create'} />
      </ModalFooter>
    </Modal>
  );
}
